/* eslint-disable react-hooks/exhaustive-deps */
import { HTMLAttributes, ReactNode, useEffect, useRef, useId } from 'react';
import './styles/DragDropZone.scss';

import { useDrop } from "react-dnd";

import { classNames, componentClassName } from '@dw/utils';

import { DragItem, DragItemType, DragItemTypes, IItemDroppedEvent, IItemHoverEvent } from "./types";
import { useDragAndDropContext } from './DragAndDropProvider';

interface IDragDropZoneProps<T> extends HTMLAttributes<HTMLDivElement> {
  onDropItem: (event: IItemDroppedEvent<T>) => void;
  /**
   * Fires on every hover frame while a matching drag is over this zone (react-dnd's `hover`
   * lifecycle, distinct from `onDropItem`'s one-shot `drop`). Purely additive: omitted, it leaves
   * this zone's drop-only behaviour exactly as before. Added for `SortableList`'s live reorder —
   * Calendar/DanceClassesSchedule never pass it, so their tick-snapping and dashed-outline
   * behaviour is unaffected.
   */
  onHoverItem?: (event: IItemHoverEvent<T>) => void;
  disabled?: boolean;
  children?: ReactNode;
  noVerticalTicks?: boolean;
  noHorizontalTicks?: boolean;
  /**
   * The react-dnd item type this zone accepts. MUST match the `dragType` of the Draggables that
   * are allowed to land here. Defaults to the shared `DragItemTypes.BOX`.
   */
  dragType?: DragItemType;
}
export function DragDropZone<T>(props: IDragDropZoneProps<T>) {

  const {
    onDropItem,
    onHoverItem,
    children,
    disabled,
    noVerticalTicks = false,
    noHorizontalTicks = false,
    dragType,
    ...divBaseProps
  } = props;

  const dropZoneId = useId();
  const dropZoneRef = useRef<HTMLDivElement>(null);
  const { removeDropZone, toggleDropZone, registerDropZone, activeDropZone } = useDragAndDropContext();

  const [{ isOver }, dropRef] = useDrop(
    () => ({
      accept: dragType ?? DragItemTypes.BOX,
      drop: (item: DragItem<T>) => onDropItem({
        itemData: item.data,
        verticalTickIndex: activeDropZone?.currentVerticalIndex,
        horizontalTickIndex: activeDropZone?.currentHorizontalIndex
      }),
      // `undefined`, not a no-op function, when the caller doesn't opt in - react-dnd treats an
      // absent `hover` key as "this target has no hover behaviour", identical to before this prop
      // existed.
      hover: onHoverItem ? (item: DragItem<T>, monitor) => onHoverItem({
        itemData: item.data,
        clientOffset: monitor.getClientOffset(),
        hoverBoundingRect: dropZoneRef.current?.getBoundingClientRect() ?? null
      }) : undefined,
      canDrop: () => !disabled,
      collect: (monitor) => ({
        isOver: monitor.isOver()
      }),
    }),
    [onDropItem, onHoverItem, disabled, activeDropZone, dragType]
  );

  useEffect(() => {
    toggleDropZone(dropZoneId, isOver);
  }, [isOver, dropZoneId]);

  useEffect(() => {
    if (!dropZoneRef.current) {
      return;
    }

    registerDropZone({
      id: dropZoneId,
      element: dropZoneRef.current,
      isOver: false,
      noVerticalTicks: noVerticalTicks,
      noHorizontalTicks: noHorizontalTicks
    });

    return () => {
      removeDropZone(dropZoneId);
    }
  }, [dropZoneId, noVerticalTicks, noHorizontalTicks]);

  const cn = componentClassName('drag-drop-zone');

  dropRef(dropZoneRef);
  return (
    <div
      {...divBaseProps}
      id={dropZoneId}
      ref={dropZoneRef}
      className={classNames(cn(), divBaseProps.className, {
        [cn('dragging-over')]: isOver
      })}
    >
      {children}
    </div>
  )
}